import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ClientField } from '../../../models/api/field/client-field';
import { pageSettings } from '../../../models/core/security/page-settings';
import { PageToken } from '../../../models/core/security/page-token';
import { ReplacePipe } from '../../../pipes/replace.pipe';
import { SecurityService } from '../../../services/security/security.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { InputComponent } from '../../core/controls/input.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-field-search',
  imports: [
    InputComponent,
    RouterModule,
    EmptyStateComponent,
    CommonModule,
    ReplacePipe,
    LinkLabelComponent,
    ButtonComponent,
    ReactiveFormsModule,
    LoadingPanelComponent,
  ],
  template: `
    <div
      class="tw-flex tw-justify-center tw-w-full tw-h-[500px] tw-w-[550px]"
      role="region"
      id="field-search-region"
      aria-labelledby="field-search-heading"
    >
      <h2 id="field-search-heading" class="tw-sr-only">
        Find claim fields search
      </h2>
      <app-loading-panel
        [state]="loading ? 'Loading' : 'Loaded'"
        loadingStyle="Dot"
        [loadingHeight]="500"
        class="tw-w-full"
        [error]="error"
      >
        <ng-template #loaded>
          <div class=" tw-w-full">
            <form
              class="tw-flex tw-items-center "
              [formGroup]="fg"
              (ngSubmit)="findFields()"
            >
              <div class="tw-w-full tw-py-2">
                <app-input
                  [control]="fg.controls.searchInput"
                  placeholder="Field name"
                  screenReaderLabel="Field name"
                  [showHelpText]="false"
                  [large]="true"
                  [showAudio]="true"
                  icon="fa-magnifying-glass fa-light"
                  classes="tw-w-full"
                  (speechComplete)="findFields()"
                  #input
                ></app-input>
              </div>
              <div class=" tw-px-2">
                <span id="field-search-find-label" class="tw-sr-only">
                  Find fields
                </span>
                <app-button
                  label="Find"
                  type="submit"
                  emphasis="Low"
                  ariaLabel="Find fields"
                  ariaLabelledBy="field-search-find-label"
                  title="Find fields"
                />
              </div>
            </form>
            @if (!searchValue) {
              <app-empty-state
                icon="fa-light fa-glasses"
                label="Find fields"
                message="Provides quick navigation to pages on this claim"
                height="300px"
              />
            } @else if (searchResults.length === 0) {
              <app-empty-state
                icon="fa-message"
                [label]="'No results for ' + searchValue"
                message="Try entering a different field name"
                height="300px"
              />
            } @else if (searchResults.length > 0) {
              <div
                class="tw-font-bold tw-text-[var(--sed-black-80-color)] tw-px-2 tw-pt-2"
              >
                <ng-container [ngPlural]="searchResultsCount">
                  <ng-template ngPluralCase="=100">
                    Over {{ searchResultsCount }} fields
                  </ng-template>
                  <ng-template ngPluralCase="other">
                    {{ searchResultsCount }} fields
                  </ng-template>
                </ng-container>
              </div>

              <div class="tw-h-[400px] tw-overflow-y-auto tw-px-2 tw-pt-2">
                @for (
                  result of searchResults;
                  track result.id;
                  let i = $index
                ) {
                  @if (pageChange(result.pageLabel, i)) {
                    <div class="tw-flex tw-items-center tw-pt-2 tw-pb-2 ">
                      <h3
                        class="tw-m-0 tw-font-bold tw-text-2xl tw-text-[var(--sed-black-80-color)]"
                      >
                        {{ result.pageLabel }}
                      </h3>
                    </div>
                  }
                  <div
                    class="tw-flex
          -tw-cursor-pointer
          tw-justify-between
          tw-items-center
          tw-py-2
          tw-px-4          
          tw-mb-2
          hover:-tw-bg-[var(--sed-grey-60-color)]
          tw-font-bold
          tw-rounded-md
          tw-bg-[var(--sed-open-grey-color)]
          -tw-border
          "
                  >
                    <div class="tw-flex tw-items-center ">
                      <i class="fa-link-horizontal fa-light tw-pr-3"></i>
                      <app-link-label (click)="onNavigate(result)">{{
                        result.path | replace: '|' : ' / '
                      }}</app-link-label>
                    </div>
                    <i class="fa-angle-right fa-regular "></i>
                  </div>
                }
              </div>
            }
          </div>
        </ng-template>
      </app-loading-panel>
    </div>
  `,
  styles: ``,
})
export class FieldSearchComponent extends BaseComponent implements OnInit {
  private security = inject(SecurityService);

  @ViewChild('input')
  searchInputControl: InputComponent;

  @Output()
  navigate = new EventEmitter<string>();

  fg = new FormGroup({
    searchInput: new FormControl<string>(null),
  });

  fields: ClientField[];

  lobFields: ClientField[];

  searchResults: ClientField[];

  searchValue: string;

  searchResultsCount = -1;

  prevPageLabel: string = null;

  loading = false;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    const claim = this.view.currentClaim();
    this.fields = await this.api.field.get();

    // Determine if this is an AC claim
    const isAcClaim =
      claim.lineOfBusiness === 'DS' && claim.coverageCode === 'AC';
    const lob = isAcClaim ? 'AC' : claim.lineOfBusiness;

    this.lobFields = this.fields.filter((x) => {
      switch (lob) {
        case 'WC':
          return x.wc;
        case 'DS':
          return x.ds;
        case 'GL':
          return x.gl;
        case 'LA':
          return x.la;
        case 'PR':
          return x.pr;
        case 'AC':
          return x.ac;
        case 'AD':
          return x.ad;
        case 'AU':
          return x.au;
        default:
          return false;
      }
    });

    // For AC claims: map accommodations-page to disability-page for security check
    if (isAcClaim) {
      this.lobFields = this.lobFields.map((field) => ({
        ...field,
        pageKey:
          field.pageKey === 'accommodations-page'
            ? 'disability-page'
            : field.pageKey,
      }));
    }

    this.lobFields = this.lobFields.filter((x) => {
      const pageToken = x.pageKey as PageToken;
      return this.security.pageAuthorize(pageToken);
    });

    let id = 1;
    for (const lob of this.lobFields) {
      lob.id = id++;
    }

    // NOTE: doing this because primeng dialog sets focus on close button
    this.dispose.timeouts = setTimeout(() => {
      this.searchInputControl?.focus();
    }, 100);
  }

  onNavigate(field: ClientField) {
    const pageToken = field.pageKey as PageToken;
    const setting = pageSettings.get(pageToken);
    if (!setting) {
      return;
    }

    const claim = this.view.currentClaim();
    const baseUrl = `/view/claim/${claim.recordSource}/${claim.recordId}/${setting.path}`;

    // Pass the field path via query params - let the page component handle tab extraction
    const queryParams: any = {};
    if (field.path) {
      queryParams['fieldPath'] = field.path;
    }

    this.router.navigate([baseUrl], {
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
    this.navigate.emit();
  }

  findFields() {
    this.searchResults = [];
    this.searchValue = this.fg.controls.searchInput.value;
    const nameLower = this.searchValue?.toLowerCase() ?? '';
    if (!nameLower) {
      this.searchResultsCount = -1;
      return;
    }

    this.searchResultsCount = 0;
    for (const field of this.lobFields) {
      const search = field.path + '|' + field.aliases;
      if (search.toLowerCase().includes(nameLower)) {
        this.searchResults.push(field);
        this.searchResultsCount++;
      }

      if (this.searchResultsCount === 100) {
        break;
      }
    }
  }

  pageChange(pageLabel: string, index: number): boolean {
    if (index === 0) {
      this.prevPageLabel = null;
    }
    const result = pageLabel !== this.prevPageLabel;
    this.prevPageLabel = pageLabel;
    return result;
  }
}
